import { connect } from 'react-redux';
import { Link } from 'react-router';
import React from 'react';
/** import third-party libraries in the section above, keep the ascending order */

import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LaunchIcon from '@mui/icons-material/Launch';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, keep the ascending order */

import { AdIndicator, CampaignIndicator } from 'widgets-v6/lifecycle-and-health-indicator';
import { BlockLoadGroup } from 'widgets-v5/load-group';
/** import widgets components in the section above, keep the ascending order */

import { DealCategory } from 'common/constants/deals';
import { formatDate_dateWithYear } from 'utils/formatting';
import { isInternalUser } from 'states/profile/business-rules';
import actions from './actions';
import InfoAlertDealAdsDisclaimer from '../info-alert-deal-ads-disclaimer';
import selector from './selector';
/** import classes and others in the section above, keep the ascending order */

const useStyles = makeStyles(theme => ({
    dialogTitleRoot: {
        padding: `10px ${theme.spacing(1)} 10px ${theme.spacing(3)}`,
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    tablePadding: {
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    },
}));

const DealAdsModal = ({ open, dispatch, deal, loading, timezone, selectedDealsCategory }) => {
    const classes = useStyles();

    const onClose = () => {
        dispatch(actions.closeDealAdsModal());
    };

    const isCuratedDeal = selectedDealsCategory === DealCategory.Curated;
    return (
        <BlockLoadGroup size="medium" isLoading={loading}>
            <Dialog fullWidth maxWidth="xl" onClose={onClose} open={open}>
                <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <div>
                            <Typography variant="h6">
                                {loading ? `Loading Deal Ads...` : `${deal.id} - ${deal.app_name}`}
                            </Typography>
                        </div>
                        <div>
                            <IconButton onClick={onClose} size="large">
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </Grid>
                </DialogTitle>
                <DialogContent>
                    {!loading && open && (
                        <DealAdsDetails
                            deal={deal}
                            classes={classes}
                            timezone={timezone}
                            isCuratedDeal={isCuratedDeal}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </BlockLoadGroup>
    );
};

const DealAdsDetails = props => {
    const { deal, classes, timezone, isCuratedDeal } = props;
    const ads = deal?.ads || [];
    const userIsInternal = isInternalUser();
    const displayOrganizationColumn = userIsInternal && isCuratedDeal;

    return (
        <React.Fragment>
            {deal && <InfoAlertDealAdsDisclaimer deal={deal} userIsInternal={userIsInternal} />}
            <Paper>
                {ads.length > 0 && (
                    <TableContainer>
                        <Table className={classes.tablePadding}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Campaign ID</TableCell>
                                    <TableCell>Campaign Status</TableCell>
                                    {displayOrganizationColumn && (
                                        <TableCell>Organization</TableCell>
                                    )}
                                    <TableCell>Campaign Name</TableCell>
                                    <TableCell>Ad ID</TableCell>
                                    <TableCell>Ad Status</TableCell>
                                    <TableCell>Ad Name</TableCell>
                                    <TableCell>Ad Start</TableCell>
                                    <TableCell>Ad End</TableCell>
                                    <TableCell>Public Inventory</TableCell>
                                    <TableCell>Link</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ads.map(ad => (
                                    <TableRow key={ad.campaign?.id + ad.id}>
                                        <TableCell>{ad.campaign?.id}</TableCell>
                                        <TableCell>
                                            <CampaignIndicator
                                                status={ad.campaign?.lifecycle.current_state}
                                            />
                                        </TableCell>
                                        {displayOrganizationColumn && (
                                            <TableCell>{ad.organizationData?.name}</TableCell>
                                        )}
                                        <TableCell>{ad.campaign?.name}</TableCell>
                                        <TableCell>{ad.id}</TableCell>
                                        <TableCell>
                                            <AdIndicator status={ad.status} />
                                        </TableCell>
                                        <TableCell>{ad.name}</TableCell>
                                        <TableCell>
                                            {formatDate_dateWithYear(ad.start, timezone)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate_dateWithYear(ad.end, timezone)}
                                        </TableCell>
                                        <TableCell>
                                            {ad.dealApplicationType === 'deal_only'
                                                ? 'Do not bid on open auctions'
                                                : 'Bid on open auctions'}
                                        </TableCell>
                                        <TableCell>
                                            {ad.campaign?.id && (
                                                <Tooltip title="View ad in a new tab">
                                                    <IconButton size="small">
                                                        <Link
                                                            to={`/campaigns/${
                                                                ad.campaign.id
                                                            }/setup/ads?search=${ad.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <LaunchIcon />
                                                        </Link>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </React.Fragment>
    );
};

export default connect(selector)(DealAdsModal);
